import axios from "axios";
import { SERVER_URL } from "Constants";
import gql from "graphql-tag";
import { IUserEntityAttributes, UserEntity } from "Models/Entities";
import { store } from "Models/Store";
import { AxiosConfigFactory } from "Util/AxiosUtil";
import { noSpaces, safeGqlSearch } from "Util/StringUtils";
import { HasCondition, IWhereCondition } from "Views/Components/ModelCollection/ModelQuery";
import { EntityControllerData, GqlListResult, PaginationParams, SearchParams, SortingParams } from "./shared";

type RequestUsersArgs = {
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	page: number,
	filterByOrgIds?: string[],
	filterByBusinessEntityIds?: string[],
	pageSize?: number,
	noPagination?: boolean
}

export type GqlUserEntityList =
	GqlListResult<{
		userEntitys: IUserEntityAttributes[];
		countUserEntitys: { number: number };
	}>;

export type UserEntityQueryOptions = PaginationParams & SortingParams & SearchParams & {
	filterByCustomerApplicationFormIds?: string[];
	filterByOrgIds?: string[];
	filterByActiveUser?: boolean;
}

// FUTURE: This api call is restful, this function should be used once the API is migrated to REST from GraphQL to avoid duplication
export async function requestUsersApi(queryParams?: UserEntityQueryOptions) {
	const url = `${SERVER_URL}/api/entity/UserEntity`;
	const requestConfig = AxiosConfigFactory.createRequestConfig(queryParams);
	return await axios.get<EntityControllerData<UserEntity>>(url, requestConfig);
}

/**
 * @deprecated
 * Use requestUsersApi(...)
 */
export async function requestUsers(args: RequestUsersArgs) {
	const {
		search,
		sortColumn,
		sortDescending,
		page,
		filterByOrgIds = [],
		filterByBusinessEntityIds = [],
		noPagination = false,
		pageSize
	} = args;

	// If there is a search included, create the search param for the query
	const comparison = 'INVARIANT_CULTURE_IGNORE_CASE';
	const searchConditions: IWhereCondition<string>[] = [];

	if (!!search) {
		const searchTermsWithSpaces = safeGqlSearch(search);
		const searchTermsWithoutSpaces = noSpaces(searchTermsWithSpaces);
		searchConditions.push({
			case: comparison, comparison: 'like', path: 'email', value: [`%${searchTermsWithSpaces}%`],
		});
		searchConditions.push({
			case: comparison, comparison: 'like', path: 'email', value: [`%${searchTermsWithoutSpaces}%`],
		});
		searchTermsWithSpaces.split(' ').forEach(searchTerm => {
			// ensure we aren't searching for a blank space
			if (searchTerm.trim() !== '') {
				['firstName', 'lastName'].forEach(path => {
					searchConditions.push({
						case: comparison, comparison: 'like', path: path, value: [`%${searchTerm}%`],
					});
				});
			}
		});
	}

	// Setup org ID filter
	const organisationConditions: IWhereCondition<string>[] = [];
	if (filterByOrgIds.length > 0) {
		organisationConditions.push({
			comparison: 'in', path: 'organisationId', value: filterByOrgIds,
		});
	}


	// Aggregate conditions into single nested list
	const allConditions: IWhereCondition<string>[][] = [];
	if (searchConditions.length > 0) {
		allConditions.push(searchConditions);
	}
	if (organisationConditions.length > 0) {
		allConditions.push(organisationConditions);
	}

	// are we sorting on conjoined columns
	const orderBy: { path: string, descending: boolean }[] = [];
	const columns = sortColumn.split(' ');
	columns.forEach(column => orderBy.push({ path: column, descending: sortDescending }));

	// Build Has conditions. (Used for filtering on business entities)
	const hasConditions: HasCondition<any>[][] = [];
	if (filterByBusinessEntityIds.length > 0) {
		const internalHasConditions: HasCondition<any>[] = [{
			path: 'businessEntitys',
			conditions: [[
				{
					path: 'businessEntityId',
					comparison: 'in',
					value: filterByBusinessEntityIds,
				},
			]],
		}];
		hasConditions.push(internalHasConditions);
	}

	const query = gql`
		query userEntity(
			$orderBy: [OrderByGraph], 
			$take: Int, 
			$skip: Int, 
			$conditions: [[WhereExpressionGraph]],
			$has: [[HasConditionType]]
		) {
			userEntitys(conditions: $conditions, has: $has, skip: $skip, take:$take, orderBy: $orderBy) {
				firstName
				lastName
				id
				email
				isActive
				userType
				phone
				legacyID
				enableEmailNotifications
				canAccessApprove
				canAccessPPSR
				canAccessIntel
				creditApprovalLimit
				organisation {
					id
					primaryBusinessEntity {
						id
						name
					}
				}
				businessEntitys {
					id
					businessEntity {
						id
						name
					}
				}
			}
			countUserEntitys(conditions: $conditions, has: $has) {
				number
			}
		}
	`;

	const finalPageSize = pageSize ?? 10;
	const response = await store.apolloClient.query<GqlUserEntityList>({
		query: query,
		fetchPolicy: 'network-only',
		variables: {
			orderBy: orderBy,
			take: !noPagination ? finalPageSize : null,
			skip: !noPagination ? finalPageSize * (page || 0) : null,
			conditions: allConditions,
			has: hasConditions,
		},
	});
	return response;
}

export async function UpdateUserApplicationTemplates(userId: string, customerApplicationTemplateIds: string[]) {
	const response = await axios.post(`${SERVER_URL}/api/entity/UserEntity/ApproveCustomerApplications`, {
		userId: userId,
		customerApplicationTemplateIds: customerApplicationTemplateIds,
	});
	return response;
}